import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Form, Input, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Dialog as ZhDialog, type UploadRequestOptions } from "zhihao-ui-react";

import request from "@/api";
import type { CustomAxiosRequestConfig } from "@/api";
import { fileService } from "@/api/services/fileService";
import { useUserStore } from "@/views/stores";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { fmtMoney, fmtNum } from "@zhihaoscm/utils";
import { formatMoney } from "@/utils/money";
import { confirmDialog } from "@/utils/confirmDialog";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { getEnterpriseName } from "@/utils/customer";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import type { OrderFinishForm, OrderVo, ServiceSpecialVo, SupplierVo } from "@zhihaoscm/entity";
import {
    Table as ZhTable,
    type TableColumnProps,
    Form as ZhFilterForm,
    Button as ZhFilterButton,
    Input as ZhFilterInput,
    Select as ZhFilterSelect
} from "zhihao-ui-react";
import { TradeOrderCancelDialog } from "./TradeOrderCancelDialog";
import { TradeOrderFinishDialog } from "./TradeOrderFinishDialog";
import { TradeOrderRemarkAttachment } from "./tradeOrder/components/TradeOrderRemarkAttachment";
import { useCountdownTimer } from "./tradeOrder/useCountdownTimer";
import "./TradeOrderPage.scss";

const orderTypeLabels: Record<number, string> = { 1: "寄售商品", 2: "团购商品", 3: "捡漏商品" };
const orderStateLabels: Record<number, string> = {
    0: "--",
    1: "待付款",
    2: "待提货",
    3: "提货中",
    4: "已完成",
    5: "已取消",
    6: "待确认支付",
    7: "拼团中",
    8: "助力中"
};
const orderOriginLabels: Record<number, string> = { 1: "客户", 2: "后台" };
const auditStateLabels: Record<number, string> = { 0: "待提交", 1: "待审核", 2: "已通过", 3: "未通过" };

/** 旧 Vue 仅在捡漏订单且助力未结束时展示助力倒计时。 */
function isShowHelpRestTime(row: OrderVo): boolean {
    return Number(row.order?.orderType) === 3 && Number(row.bargainEndFlag) === 0;
}

/** 按旧 Vue 格式化捡漏订单的助力剩余时间。 */
function formatHelpRemainingTime(endTimeValue?: string): string {
    if (!endTimeValue) return "--";

    const remaining = new Date(endTimeValue).getTime() - Date.now();
    if (remaining <= 0 || !Number.isFinite(remaining)) return "--";

    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor((remaining % 86400000) / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

    return days > 0 ? `${days}天${formattedTime}` : formattedTime;
}

/** 按旧 Vue goProductDetail 的订单类型规则返回商品详情地址。 */
function getTradeOrderProductDetailPath(
    productId: number | string | undefined,
    orderType: number | string | undefined
): string | undefined {
    if (productId == null) return undefined;

    const detailPathByOrderType: Record<number, string> = {
        1: "/productManagement/consignment/detail",
        2: "/productManagement/groupPurchase/detail",
        3: "/productManagement/bargain/detail"
    };
    const detailPath = detailPathByOrderType[Number(orderType)];
    return detailPath ? `${detailPath}?id=${encodeURIComponent(String(productId))}` : undefined;
}

export interface TradeOrderQuery {
    page: number;
    size: number;
    id?: string;
    productId?: string;
    productTitle?: string;
    keyword?: string;
    orderType?: number;
    supplierId?: number;
    states?: number[];
    orderOrigin?: number;
    auditState?: number[];
    handlerName?: string;
    beginTime?: string;
    endTime?: string;
    sortKey?: "order_time" | "updated_time";
    sortOrder?: "asc" | "desc";
}

export interface TradeOrderPageResult {
    list: OrderVo[];
    total: number;
}

export interface TradeOrderRemarkForm {
    orderId: number;
    fileId: number;
    remark?: string;
}

export interface TradeOrderAssignForm {
    orderId: number | string;
    handlerId: string;
}

export interface TradeOrderAuditForm {
    id: number | string;
    state: boolean;
    auditDesc: string;
}

export type TradeOrderFinishForm = OrderFinishForm;

export interface TradeOrderPickedUpTonForm {
    pickedUpTon: number;
    settlementIds?: number[];
}

export interface TradeOrderOfflinePaymentForm {
    orderId: string;
    paymentFileId: number;
}

export interface TradeOrderSupplierQuery {
    page: number;
    size: number;
    supplierName?: string;
}

export interface TradeOrderSupplierPageResult {
    list: SupplierVo[];
    total: number;
}

export interface TradeOrderPageProps {
    addOrderRemark?: (params: TradeOrderRemarkForm) => Promise<void>;
    assignOrder?: (params: TradeOrderAssignForm) => Promise<void>;
    auditOrder?: (params: TradeOrderAuditForm) => Promise<void>;
    cancelOrder?: (id: number | string) => Promise<void>;
    deleteOrder?: (id: number | string) => Promise<void>;
    exportOrders?: (query: TradeOrderQuery) => Promise<void>;
    finishOrder?: (id: number | string, form: TradeOrderFinishForm) => Promise<void>;
    loadOrders?: (query: TradeOrderQuery) => Promise<TradeOrderPageResult>;
    loadCommissioners?: () => Promise<ServiceSpecialVo[]>;
    loadSuppliers?: (query: TradeOrderSupplierQuery) => Promise<TradeOrderSupplierPageResult>;
    offlinePayment?: (params: TradeOrderOfflinePaymentForm) => Promise<void>;
    permissions?: string[];
    updatePickedUpTon?: (id: number | string, form: TradeOrderPickedUpTonForm) => Promise<void>;
}

interface TradeOrderFilterFormValues {
    id?: string;
    productId?: string;
    productTitle?: string;
    keyword?: string;
    orderType?: number;
    states?: number[];
    orderOrigin?: number;
    auditState?: number[];
    handlerName?: string;
    beginDate?: string;
    endDate?: string;
}

/** 将日期组件返回的字符串、原生 Date 或 Dayjs 值统一为查询栏展示格式。 */
function formatTradeOrderDateValue(value: unknown): string | null {
    if (typeof value === "string") return value || null;
    if (value instanceof Date) return dateFormat(value, "YYYY-MM-DD");
    if (value && typeof value === "object" && "format" in value && typeof value.format === "function") {
        return value.format("YYYY-MM-DD") || null;
    }
    return null;
}

/** 兼容日期组件在不同交互阶段返回 formattedValue 或原始日期值。 */
export function normalizeTradeOrderDateRange(
    value: unknown,
    formattedValue: readonly (string | null | undefined)[] | null | undefined
): [string | null, string | null] {
    const rawValues = Array.isArray(value) ? value : [];
    const formattedValues = Array.isArray(formattedValue) ? formattedValue : [];
    return [
        formatTradeOrderDateValue(formattedValues[0]) || formatTradeOrderDateValue(rawValues[0]),
        formatTradeOrderDateValue(formattedValues[1]) || formatTradeOrderDateValue(rawValues[1])
    ];
}

/** 复用 Vue 交易订单分页接口，保持筛选参数名称和分页结构不变。 */
export async function loadTradeOrders(query: TradeOrderQuery): Promise<TradeOrderPageResult> {
    const response = await request.service.get<TradeOrderPageResult>("/admin/order/paging", { params: query });
    return response.data;
}

/** 复用 Vue 取消交易订单接口。 */
export async function cancelTradeOrder(id: number | string): Promise<void> {
    await request.service.put(`/admin/order/cancel/${id}`);
}

/** 复用 Vue 新增订单备注接口，附件 ID 和备注字段保持原契约。 */
export async function addTradeOrderRemark(params: TradeOrderRemarkForm): Promise<void> {
    await request.service.post("/admin/order-remark", params);
}

/** 复用 Vue 交易订单专员指派接口。 */
export async function assignTradeOrder(params: TradeOrderAssignForm): Promise<void> {
    await request.service.put(`/admin/order/assign/${params.orderId}/${params.handlerId}`);
}

/** 复用 Vue 补录订单审核接口，保留审核状态和意见字段。 */
export async function auditTradeOrder(params: TradeOrderAuditForm): Promise<void> {
    await request.service.put(`/admin/order/audit/record-order/${params.id}`, {
        state: params.state,
        auditDesc: params.auditDesc
    });
}

/** 复用 Vue 完成交易订单接口，保留完成表单中的吨数和结算凭证字段。 */
export async function finishTradeOrder(id: number | string, form: TradeOrderFinishForm): Promise<void> {
    await request.service.put(`/admin/order/finish/${id}`, form);
}

/** 复用 Vue 完成订单前的确认接口，决定提货中订单展示的纯文案提示。 */
export async function loadTradeOrderFinishConfirm(id: number | string): Promise<{ hasPendingCancelDelivery: boolean }> {
    const response = await request.service.get<{ hasPendingCancelDelivery: boolean }>(
        `/admin/order/finish-confirm/${id}`
    );
    return response.data;
}

/** 复用 Vue 修改已提货吨数接口，保持结算凭证数组字段。 */
export async function updateTradeOrderPickedUpTon(id: number | string, form: TradeOrderPickedUpTonForm): Promise<void> {
    await request.service.put(`/admin/order/update/ton/${id}`, form);
}

/** 复用 Vue 删除交易订单接口。 */
export async function deleteTradeOrder(id: number | string): Promise<void> {
    await request.service.delete(`/admin/order/${id}`);
}

/** 从响应头中读取 Vue 导出接口提供的文件名，缺失或异常时使用稳定的中文默认名。 */
function getTradeOrderExportFilename(contentDisposition?: string): string {
    const fallback = "交易订单.xlsx";
    if (!contentDisposition) return fallback;

    const encodedFilename = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i)?.[1]?.trim();
    const filename = encodedFilename || contentDisposition.match(/filename=([^;]+)/i)?.[1]?.trim();
    if (!filename) return fallback;

    const normalizedFilename = filename.replace(/^['"]|['"]$/g, "");
    try {
        return decodeURIComponent(normalizedFilename) || fallback;
    } catch {
        return normalizedFilename || fallback;
    }
}

/** 将 Blob 转换为浏览器下载，点击完成后立即清理锚点和临时 URL。 */
function downloadTradeOrderBlob(blob: Blob, filename: string): void {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);

    try {
        link.click();
    } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    }
}

/** 复用 Vue 交易订单导出接口并触发真实文件下载。 */
export async function exportTradeOrders(query: TradeOrderQuery): Promise<void> {
    const response = await request.service.get<Blob, AxiosResponse<Blob>>("/admin/order/export", {
        params: query,
        responseType: "blob",
        returnRawResponse: true
    } as CustomAxiosRequestConfig);
    const filename = getTradeOrderExportFilename(response.headers["content-disposition"]);
    downloadTradeOrderBlob(response.data, filename);
}

/** 复用 Vue 交易订单供应商远程分页接口。 */
export async function loadTradeOrderSuppliers(query: TradeOrderSupplierQuery): Promise<TradeOrderSupplierPageResult> {
    const response = await request.service.get<TradeOrderSupplierPageResult>("/admin/supplier-admin/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 交易订单专员候选接口。 */
export async function loadTradeOrderCommissioners(): Promise<ServiceSpecialVo[]> {
    const response = await request.service.get<ServiceSpecialVo[]>("/admin/order/find/specials");
    return response.data;
}

/** 复用旧 Vue 线下支付接口，提交订单编号和单个付款凭证 ID。 */
export async function submitTradeOrderOfflinePayment(params: TradeOrderOfflinePaymentForm): Promise<void> {
    await request.service.post("/order/offline-pay", params);
}

const defaultQuery: TradeOrderQuery = { page: 1, size: 10 };

/** 将 zhihao 表格排序字段转换为旧 Vue 订单分页接口参数。 */
function getTradeOrderSortParams(sort: { prop?: string; order: "ascending" | "descending" | null }) {
    const sortKey = sort.prop === "orderTime" ? "order_time" : sort.prop === "updatedTime" ? "updated_time" : undefined;
    if (!sortKey || !sort.order) return {};
    return { sortKey, sortOrder: sort.order === "ascending" ? "asc" : "desc" } as const;
}

/** 将 Vue 交易订单分页返回的枚举字段统一为 React 操作和文案使用的数字。 */
function normalizeTradeOrderRows(rows?: OrderVo[]): OrderVo[] {
    return (rows || []).map(row => ({
        ...row,
        order: {
            ...row.order,
            orderType: Number(row.order.orderType),
            state: Number(row.order.state),
            beforeFinishState: Number(row.order.beforeFinishState),
            orderOrigin: Number(row.order.orderOrigin),
            auditState: Number(row.order.auditState),
            payType: Number(row.order.payType)
        },
        bargainEndFlag: Number(row.bargainEndFlag)
    }));
}

/** 交易订单列表，完整复用 Vue 字段、筛选、导出权限和分页行为。 */
export function TradeOrderPage({
    addOrderRemark = addTradeOrderRemark,
    assignOrder = assignTradeOrder,
    auditOrder = auditTradeOrder,
    cancelOrder = cancelTradeOrder,
    deleteOrder = deleteTradeOrder,
    exportOrders = exportTradeOrders,
    finishOrder = finishTradeOrder,
    loadOrders = loadTradeOrders,
    loadCommissioners = loadTradeOrderCommissioners,
    loadSuppliers = loadTradeOrderSuppliers,
    offlinePayment = submitTradeOrderOfflinePayment,
    permissions,
    updatePickedUpTon = updateTradeOrderPickedUpTon
}: TradeOrderPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    const { restTimeKey } = useCountdownTimer();
    const canCreate = currentPermissions.includes("ROLE_TRADE_ORDER_DEAL");
    const canExport = currentPermissions.includes("ROLE_TRADE_ORDER_EXPORT");
    const [items, setItems] = useState<OrderVo[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<TradeOrderQuery>(defaultQuery);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [filterForm] = Form.useForm<TradeOrderFilterFormValues>();
    const [supplierOptions, setSupplierOptions] = useState<SupplierVo[]>([]);
    const [selectedSupplierId, setSelectedSupplierId] = useState<number | undefined>();
    const [supplierKeyword, setSupplierKeyword] = useState("");
    // 保持旧 Vue 下单日期范围的展示值，并在提交时转换为接口时间边界。
    const [orderDateRange, setOrderDateRange] = useState<[string | null, string | null]>([null, null]);
    const [remarkTarget, setRemarkTarget] = useState<OrderVo | null>(null);
    const [remarkOpen, setRemarkOpen] = useState(false);
    const [remarkFileId, setRemarkFileId] = useState("");
    const [remarkFileName, setRemarkFileName] = useState("");
    const [remarkFilePath, setRemarkFilePath] = useState("");
    const [remarkContent, setRemarkContent] = useState("");
    const [remarkError, setRemarkError] = useState<string | null>(null);
    const [assignTarget, setAssignTarget] = useState<OrderVo | null>(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [commissioners, setCommissioners] = useState<ServiceSpecialVo[]>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState("");
    const [assignKeyword, setAssignKeyword] = useState("");
    const [assignError, setAssignError] = useState<string | null>(null);
    const [auditTarget, setAuditTarget] = useState<OrderVo | null>(null);
    const [auditOpen, setAuditOpen] = useState(false);
    const [auditDecision, setAuditDecision] = useState<boolean | null>(null);
    const [auditDescription, setAuditDescription] = useState("");
    const [auditError, setAuditError] = useState<string | null>(null);
    const [pickedUpTonTarget, setPickedUpTonTarget] = useState<OrderVo | null>(null);
    const [pickedUpTonOpen, setPickedUpTonOpen] = useState(false);
    const [pickedUpTonFiles, setPickedUpTonFiles] = useState<Array<{ id: number; name: string; path?: string }>>([]);
    const [pickedUpTonError, setPickedUpTonError] = useState<string | null>(null);
    const [finishTarget, setFinishTarget] = useState<OrderVo | null>(null);
    const [finishSubmitting, setFinishSubmitting] = useState(false);
    const [finishError, setFinishError] = useState<string | null>(null);
    const [cancelTarget, setCancelTarget] = useState<OrderVo | null>(null);
    const [cancelSubmitting, setCancelSubmitting] = useState(false);
    const [cancelError, setCancelError] = useState<string | null>(null);
    const [offlinePaymentTarget, setOfflinePaymentTarget] = useState<OrderVo | null>(null);
    const [offlinePaymentOpen, setOfflinePaymentOpen] = useState(false);
    const [offlinePaymentFileId, setOfflinePaymentFileId] = useState("");
    const [offlinePaymentSubmitting, setOfflinePaymentSubmitting] = useState(false);
    const [offlinePaymentError, setOfflinePaymentError] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadOrders(query)
            .then(result => {
                if (!disposed) {
                    setItems(normalizeTradeOrderRows(result.list));
                    setTotal(result.total || 0);
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setItems([]);
                    setTotal(0);
                    setErrorMessage("交易订单加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadOrders, query]);

    const submitFilters = (values: TradeOrderFilterFormValues) => {
        // 显式映射 Vue 搜索字段，避免把展示用日期和逗号文本直接发送到接口。
        const filters: Omit<TradeOrderQuery, "page" | "size"> = {};
        if (values.id?.trim()) filters.id = values.id.trim();
        if (values.productId?.trim()) filters.productId = values.productId.trim();
        if (values.productTitle?.trim()) filters.productTitle = values.productTitle.trim();
        if (values.keyword?.trim()) filters.keyword = values.keyword.trim();
        if (values.orderType != null) filters.orderType = Number(values.orderType);
        if (selectedSupplierId != null) filters.supplierId = selectedSupplierId;
        if (values.states?.length) filters.states = values.states.map(Number);
        if (values.auditState?.length) filters.auditState = values.auditState.map(Number);
        if (values.orderOrigin != null) filters.orderOrigin = Number(values.orderOrigin);
        if (values.handlerName?.trim()) filters.handlerName = values.handlerName.trim();
        const [beginDate, endDate] = orderDateRange;
        if (beginDate?.trim()) filters.beginTime = `${beginDate.trim()} 00:00:00`;
        if (endDate?.trim()) filters.endTime = `${endDate.trim()} 23:59:59`;
        setErrorMessage(null);
        setQuery({ page: 1, size: 10, ...filters });
    };

    const searchSupplierOptions = async (keyword = "") => {
        // Bug 12584 要求供应商下拉打开即可展示候选；空关键词沿用 Vue 分页接口查询第一页。
        try {
            const result = await loadSuppliers({
                page: 1,
                size: 50,
                ...(keyword.trim() ? { supplierName: keyword.trim() } : {})
            });
            setSupplierOptions(result.list || []);
        } catch {
            setErrorMessage("供应商候选查询失败");
        }
    };

    const resetFilters = () => {
        filterForm.resetFields();
        setOrderDateRange([null, null]);
        setQuery(defaultQuery);
    };

    const submitExport = async () => {
        try {
            // 沿用 Vue 导出规则：提交当前筛选条件，并保留当前订单列表。
            await exportOrders(query);
            notifyOperationSuccess("导出成功");
        } catch {
            // 导出接口失败时反馈页面级错误，避免产生未处理的异步异常。
            setErrorMessage("交易订单导出失败");
        }
    };

    const openCancelDialog = (row: OrderVo) => {
        // 旧 Vue 弹窗按当前订单行展示取消提示，打开时清理上一次请求状态。
        setCancelTarget(row);
        setCancelError(null);
    };

    const closeCancelDialog = () => {
        if (cancelSubmitting) return;
        setCancelTarget(null);
        setCancelError(null);
    };

    const submitCancelOrder = async () => {
        const id = cancelTarget?.order?.id;
        if (id == null) return;
        setCancelSubmitting(true);
        setCancelError(null);
        try {
            await cancelOrder(id);
            setCancelTarget(null);
            setQuery(current => ({ ...current }));
        } catch {
            // 保持 Vue 弹窗打开并反馈失败，允许用户重新确认或取消。
            setCancelError("取消订单失败");
        } finally {
            setCancelSubmitting(false);
        }
    };

    const confirmDeleteOrder = (id: number | string) => {
        void confirmDialog("是否删除订单，删除后此订单不在展示", "提示", {
            onOk: async () => {
                try {
                    await deleteOrder(id);
                    setQuery(current => ({ ...current }));
                } catch {
                    setErrorMessage("删除订单失败");
                    throw new Error("删除订单失败");
                }
            }
        });
    };

    const openOrderRemark = (row: OrderVo) => {
        // 新增备注沿用 Vue 弹窗的空表单行为，提交后刷新当前分页。
        setRemarkTarget(row);
        setRemarkFileId("");
        setRemarkFileName("");
        setRemarkFilePath("");
        setRemarkContent("");
        setRemarkError(null);
        setRemarkOpen(true);
    };

    const submitOrderRemark = async () => {
        const orderId = remarkTarget?.order?.id;
        const normalizedFileId = remarkFileId.trim();
        const fileId = Number(normalizedFileId);
        if (orderId == null || !normalizedFileId || !Number.isFinite(fileId)) {
            setRemarkError("请上传附件");
            return;
        }
        try {
            const normalizedRemark = remarkContent.trim();
            await addOrderRemark({
                orderId: Number(orderId),
                fileId,
                ...(normalizedRemark ? { remark: normalizedRemark } : {})
            });
            setRemarkOpen(false);
            setRemarkTarget(null);
            setQuery(current => ({ ...current }));
        } catch {
            setRemarkError("订单备注保存失败");
        }
    };

    /** 使用 Vue 文件服务上传订单备注附件，并将返回的文件 ID 回填到备注提交表单。 */
    const uploadOrderRemarkFile = async (options: UploadRequestOptions) => {
        const file = options.file as File;
        const formData = new FormData();
        formData.append("file", file);
        try {
            // 备注附件服务仅在用户选择文件后加载，避免静态导入影响交易订单其他回归入口。
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            if (!uploadedFile.id) throw new Error("上传响应缺少文件 ID");
            setRemarkFileId(String(uploadedFile.id));
            setRemarkFileName(file.name);
            setRemarkFilePath(uploadedFile.path || "");
            setRemarkError(null);
            options.onSuccess(uploadedFile);
        } catch (error) {
            setRemarkError("订单备注附件上传失败");
            options.onError(error instanceof Error ? error : new Error("订单备注附件上传失败"));
        }
    };

    /** 打开刚上传的备注附件，保持旧 Vue SingleUpload 的查看/下载语义。 */
    const previewOrderRemarkFile = () => {
        if (remarkFilePath) window.open(remarkFilePath, "_blank", "noopener,noreferrer");
    };

    /** 删除当前弹窗附件并清空提交所需的文件 ID。 */
    const removeOrderRemarkFile = () => {
        setRemarkFileId("");
        setRemarkFileName("");
        setRemarkFilePath("");
    };

    const openAssignDialog = async (row: OrderVo) => {
        // 打开弹窗后按 Vue 页面约定加载可指派专员，并回显当前处理人。
        setAssignTarget(row);
        // 指派弹窗默认不选中专员，用户选定候选项后才能确认。
        setSelectedHandlerId("");
        setAssignKeyword("");
        setAssignError(null);
        setAssignOpen(true);
        try {
            setCommissioners((await loadCommissioners()) || []);
        } catch {
            setCommissioners([]);
            setAssignError("订单专员查询失败");
        }
    };

    const submitOrderAssign = async () => {
        const orderId = assignTarget?.order?.id;
        if (orderId == null || !selectedHandlerId) return;
        try {
            await assignOrder({ orderId, handlerId: selectedHandlerId });
            notifyOperationSuccess("指派成功");
            setAssignOpen(false);
            setAssignTarget(null);
            setAssignKeyword("");
            setQuery(current => ({ ...current }));
        } catch {
            setAssignError("订单专员指派失败");
        }
    };

    const openAuditDialog = (row: OrderVo) => {
        // Vue 审核页先选择同意/拒绝，再按拒绝规则要求审核意见。
        setAuditTarget(row);
        setAuditDecision(null);
        setAuditDescription("");
        setAuditError(null);
        setAuditOpen(true);
    };

    const submitOrderAudit = async () => {
        const id = auditTarget?.order?.id;
        const description = auditDescription.trim();
        if (id == null || auditDecision == null) {
            setAuditError("请选择审核结果");
            return;
        }
        if (!auditDecision && !description) {
            setAuditError("请输入驳回原因");
            return;
        }
        try {
            await auditOrder({ id, state: auditDecision, auditDesc: description });
            setAuditOpen(false);
            setAuditTarget(null);
            setQuery(current => ({ ...current }));
        } catch {
            setAuditError("订单审核失败");
        }
    };

    const openFinishDialog = async (row: OrderVo) => {
        // 旧 Vue 仅对待提货订单打开字段表单，提货中订单走纯文案确认框。
        if (Number(row.order.state) === 2) {
            setFinishTarget(row);
            setFinishError(null);
            return;
        }

        try {
            const confirmInfo = await loadTradeOrderFinishConfirm(row.order.id);
            const confirmMessage = confirmInfo.hasPendingCancelDelivery
                ? "是否完成，完成后待发起和待确认的提货将自动取消且不能再提货。"
                : "是否完成，完成后不能再提货";
            const confirmed = await confirmDialog(confirmMessage, "提示", {
                className: "trade-order-finish-confirm-box"
            });
            if (!confirmed) return;
            await finishOrder(row.order.id, {});
            setQuery(current => ({ ...current }));
        } catch {
            setErrorMessage("完成订单失败");
        }
    };

    const closeFinishDialog = () => {
        if (finishSubmitting) return;
        setFinishTarget(null);
        setFinishError(null);
    };

    const submitFinishOrder = async (form: OrderFinishForm) => {
        const id = finishTarget?.order?.id;
        if (id == null) return;
        setFinishSubmitting(true);
        setFinishError(null);
        try {
            await finishOrder(id, form);
            setFinishTarget(null);
            setQuery(current => ({ ...current }));
        } catch {
            // 请求失败时保留弹窗，允许用户修正表单后重试。
            setFinishError("完成订单失败");
        } finally {
            setFinishSubmitting(false);
        }
    };

    const openPickedUpTonDialog = (row: OrderVo) => {
        // 仅对 Vue 已完成且完成前为待提货的订单展示修改入口。
        setPickedUpTonTarget(row);
        setPickedUpTonFiles([]);
        setPickedUpTonError(null);
        setPickedUpTonOpen(true);
        // 编辑时显示已关联凭证的文件名，同时保留原有附件 ID。
        const settlementIds = row.order?.settlementIds || [];
        if (settlementIds.length) {
            void fileService
                .getFileByIds(settlementIds)
                .then(files => setPickedUpTonFiles(files.map(({ id, name, path }) => ({ id, name, path }))))
                .catch(() => setPickedUpTonFiles([]));
        }
    };

    const submitPickedUpTon = async (form: OrderFinishForm) => {
        const id = pickedUpTonTarget?.order?.id;
        const ton = Number(form.pickedUpTon);
        if (id == null || !Number.isFinite(ton) || ton <= 0) {
            setPickedUpTonError("请输入已提货吨数");
            return;
        }
        try {
            await updatePickedUpTon(id, {
                pickedUpTon: ton,
                ...(form.settlementIds?.length ? { settlementIds: form.settlementIds } : {})
            });
            setPickedUpTonOpen(false);
            setPickedUpTonTarget(null);
            setQuery(current => ({ ...current }));
        } catch {
            setPickedUpTonError("已提货吨数修改失败");
        }
    };

    /** 旧 Vue 线下支付入口仅面向待付款捡漏订单，并使用订单处理权限。 */
    const openOfflinePaymentDialog = (row: OrderVo) => {
        setOfflinePaymentTarget(row);
        setOfflinePaymentFileId("");
        setOfflinePaymentError(null);
        setOfflinePaymentOpen(true);
    };

    const closeOfflinePaymentDialog = () => {
        if (offlinePaymentSubmitting) return;
        setOfflinePaymentOpen(false);
        setOfflinePaymentTarget(null);
        setOfflinePaymentFileId("");
        setOfflinePaymentError(null);
    };

    const submitOfflinePayment = async () => {
        const orderId = offlinePaymentTarget?.order?.id;
        const normalizedFileId = offlinePaymentFileId.trim();
        const paymentFileId = Number(normalizedFileId);
        if (orderId == null || !normalizedFileId || !Number.isFinite(paymentFileId)) {
            setOfflinePaymentError("请上传付款凭证");
            return;
        }

        setOfflinePaymentSubmitting(true);
        setOfflinePaymentError(null);
        try {
            await offlinePayment({ orderId: String(orderId), paymentFileId });
            setOfflinePaymentOpen(false);
            setOfflinePaymentTarget(null);
            setOfflinePaymentFileId("");
            setQuery(current => ({ ...current }));
        } catch {
            setOfflinePaymentError("线下支付提交失败");
        } finally {
            setOfflinePaymentSubmitting(false);
        }
    };

    return (
        <section aria-label="trade-order-page" className="admin-trade-list-page flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable
                    className="flex-1"
                    data={items}
                    onSortChange={sort =>
                        setQuery(current => ({ ...current, page: 1, ...getTradeOrderSortParams(sort) }))
                    }
                    pagination={false}
                    rowKey={row => String(row.order?.id)}
                    columns={normalizeOperationColumns(
                        [
                            {
                                prop: "id",
                                label: "订单编号",
                                width: 176,
                                render: ({ row }) =>
                                    row.order?.id && row.order.state ? (
                                        <Link to={`/tradeManage/tradeOrder/detail?id=${row.order.id}`}>
                                            {row.order.id}
                                        </Link>
                                    ) : (
                                        row.order?.id || "-"
                                    )
                            },
                            {
                                prop: "productId",
                                label: "商品编号",
                                width: 176,
                                render: ({ row }) => {
                                    const productId = row.order?.productId;
                                    const detailPath = getTradeOrderProductDetailPath(productId, row.order?.orderType);
                                    return detailPath ? <Link to={detailPath}>{productId}</Link> : productId || "--";
                                }
                            },
                            {
                                prop: "productTitle",
                                label: "商品标题",
                                width: 176,
                                render: ({ row }) => row.order?.productInfo?.title || "--"
                            },
                            {
                                prop: "orderType",
                                label: "订单类型",
                                width: 120,
                                render: ({ row }) => orderTypeLabels[row.order?.orderType] || "--"
                            },
                            {
                                prop: "customer",
                                label: "采购用户",
                                minWidth: 350,
                                render: ({ row }) => {
                                    const enterprise = row.order?.customerEnterprise;
                                    const customerText = `${getEnterpriseName(enterprise)}(${enterprise?.mobile || "--"})`;
                                    return row.order?.customerId ? (
                                        <Link to={`/lianYunUser/userInfo?userId=${row.order.customerId}`}>
                                            {customerText}
                                        </Link>
                                    ) : (
                                        customerText
                                    );
                                }
                            },
                            {
                                prop: "ton",
                                label: "采购吨数(吨)",
                                minWidth: 106,
                                align: "right",
                                headerAlign: "right",
                                render: ({ row }) => fmtNum(row.order?.ton)
                            },
                            {
                                prop: "pickedUpTon",
                                label: "实际结算吨数（吨）",
                                minWidth: 162,
                                align: "right",
                                headerAlign: "right",
                                // 旧 Vue 使用订单主体的 pickedUpTon 字段作为实际结算吨数。
                                render: ({ row }) => fmtNum(row.order?.pickedUpTon)
                            },
                            {
                                prop: "unitPrice",
                                label: "交易单价(元/吨)",
                                minWidth: 134,
                                align: "right",
                                headerAlign: "right",
                                render: ({ row }) => fmtMoney(row.order?.unitPrice)
                            },
                            {
                                prop: "estimatedAmount",
                                label: "预估金额(元)",
                                width: 162,
                                align: "right",
                                headerAlign: "right",
                                render: ({ row }) => fmtMoney(row.order?.estimatedAmount)
                            },
                            {
                                prop: "settlementAmount",
                                label: "结算金额（元）",
                                width: 162,
                                align: "right",
                                headerAlign: "right",
                                render: ({ row }) => fmtMoney(row.settlementAmount)
                            },
                            {
                                prop: "actualReceivedAmount",
                                label: "实收款(元)",
                                minWidth: 92,
                                align: "right",
                                headerAlign: "right",
                                // 实收款沿用 Vue：实际收款减去已退款金额。
                                render: ({ row }) =>
                                    fmtMoney(
                                        Number(row.order?.actualReceivedAmount || 0) -
                                            Number(row.order?.refundAmount || 0)
                                    )
                            },
                            {
                                prop: "handlerName",
                                label: "订单专员",
                                minWidth: 92,
                                render: ({ row }) => row.order?.handlerName || "--"
                            },
                            {
                                prop: "updatedTime",
                                label: "更新时间",
                                width: 154,
                                sortable: true,
                                render: ({ row }) => dateFormat(row.order?.updatedTime, DATETIME_PICKER_FORMAT)
                            },
                            {
                                prop: "orderTime",
                                label: "下单时间",
                                width: 154,
                                sortable: true,
                                render: ({ row }) => dateFormat(row.order?.orderTime, DATETIME_PICKER_FORMAT)
                            },
                            {
                                prop: "state",
                                label: "订单状态",
                                width: 176,
                                render: ({ row }) => {
                                    const state = orderStateLabels[row.order?.state] || "--";
                                    const bargainEndTime = row.order?.productInfo?.bargainEndTime;

                                    return (
                                        <div className="trade-order-state-wrap">
                                            <span>{state}</span>
                                            {isShowHelpRestTime(row) && bargainEndTime ? (
                                                <span key={restTimeKey} className="trade-order-rest-time">
                                                    剩余助力时间 {formatHelpRemainingTime(bargainEndTime)}
                                                </span>
                                            ) : null}
                                        </div>
                                    );
                                }
                            },
                            {
                                prop: "orderOrigin",
                                label: "订单来源",
                                width: 120,
                                render: ({ row }) => orderOriginLabels[row.order?.orderOrigin] || "--"
                            },
                            {
                                prop: "auditState",
                                label: "审核状态",
                                width: 120,
                                // Vue ProTable 对 auditState=0（未提交）按空值展示“--”，仅保留非零审核状态文案。
                                render: ({ row }) =>
                                    row.order?.auditState ? auditStateLabels[row.order.auditState] || "--" : "--"
                            },
                            {
                                prop: "operation",
                                label: "操作",
                                fixed: "right",
                                // 旧 Vue 固定操作列最终宽度为 218px，避免 Ant 动态测量产生 5px 偏移。
                                className: "compact",
                                width: 218,
                                render: ({ row }) => {
                                    const order = row.order;
                                    if (!order?.id) return null;
                                    const canManage = currentPermissions.includes("ROLE_TRADE_ORDER_MANAGE");
                                    const canDeal = currentPermissions.includes("ROLE_TRADE_ORDER_DEAL");
                                    const canCancel = currentPermissions.includes("ROLE_TRADE_ORDER_CANCEL");
                                    const canDelete = currentPermissions.includes("ROLE_TRADE_ORDER_DELETE");
                                    const canRead = currentPermissions.includes("ROLE_TRADE_ORDER_R");
                                    const canOfflinePayment = order.state === 1 && order.orderType === 3 && canDeal;
                                    const canAssign = order.state !== 5 && order.state !== 0 && canManage;
                                    const canAudit = order.auditState === 1 && canManage;
                                    const canEdit =
                                        order.orderOrigin !== 1 && !(order.auditState === 1 && !order.state) && canDeal;
                                    const canFinish = [2, 3].includes(order.state) && canManage;
                                    const canEditPickedUpTon =
                                        order.beforeFinishState === 2 && order.state === 4 && canManage;
                                    const canRemark = order.state !== 0 && canDeal;
                                    const canCancelOrder =
                                        [1, 6, 2, 3, 7].includes(order.state) &&
                                        !(order.orderType === 3 && row.bargainEndFlag === 0) &&
                                        canCancel;
                                    const canDeleteOrder =
                                        ([4, 5].includes(order.state) && canDelete) ||
                                        (!!order.auditState && !order.state && canDeal);
                                    // 复用 Vue TableColumn 的折叠规则：超过三项时保留前两项，其余收进“更多”。
                                    return (
                                        <Space wrap>
                                            {canAudit ? (
                                                <Button
                                                    aria-label={`审核订单${order.id}`}
                                                    type="link"
                                                    onClick={() => openAuditDialog(row)}
                                                >
                                                    审核
                                                </Button>
                                            ) : null}
                                            {canEdit ? (
                                                <Link
                                                    aria-label={`编辑订单${order.id}`}
                                                    to={`/tradeManage/tradeOrder/edit?id=${order.id}`}
                                                >
                                                    编辑
                                                </Link>
                                            ) : null}
                                            {canAssign ? (
                                                <Button
                                                    aria-label={`指派订单${order.id}`}
                                                    type="link"
                                                    onClick={() => void openAssignDialog(row)}
                                                >
                                                    {order.handlerId ? "变更指派" : "指派"}
                                                </Button>
                                            ) : null}
                                            {canOfflinePayment ? (
                                                <Button
                                                    aria-label={`线下支付订单${order.id}`}
                                                    type="link"
                                                    onClick={() => openOfflinePaymentDialog(row)}
                                                >
                                                    线下支付
                                                </Button>
                                            ) : null}
                                            {canFinish ? (
                                                <Button
                                                    aria-label={`完成订单${order.id}`}
                                                    type="link"
                                                    onClick={() => openFinishDialog(row)}
                                                >
                                                    完成
                                                </Button>
                                            ) : null}
                                            {canEditPickedUpTon ? (
                                                <Button
                                                    aria-label={`修改提货吨数${order.id}`}
                                                    type="link"
                                                    onClick={() => openPickedUpTonDialog(row)}
                                                >
                                                    修改提货吨数
                                                </Button>
                                            ) : null}
                                            {canRemark ? (
                                                <Button
                                                    aria-label={`订单备注${order.id}`}
                                                    type="link"
                                                    onClick={() => openOrderRemark(row)}
                                                >
                                                    订单备注
                                                </Button>
                                            ) : null}
                                            {canCancelOrder ? (
                                                <Button
                                                    aria-label={`取消订单${order.id}`}
                                                    type="link"
                                                    onClick={() => openCancelDialog(row)}
                                                >
                                                    取消订单
                                                </Button>
                                            ) : null}
                                            {canDeleteOrder ? (
                                                <Button
                                                    aria-label={`删除订单${order.id}`}
                                                    type="link"
                                                    onClick={() => confirmDeleteOrder(order.id)}
                                                >
                                                    删除
                                                </Button>
                                            ) : null}
                                            {row.hasRelatedInvoice && canRead ? (
                                                <Link
                                                    aria-label={`查看发票${order.id}`}
                                                    to={`/tradeManage/invoiceManage?orderId=${order.id}`}
                                                >
                                                    查看发票
                                                </Link>
                                            ) : null}
                                        </Space>
                                    );
                                }
                            }
                        ] as TableColumnProps<OrderVo>[],
                        items
                    )}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                交易订单
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <ZhFilterForm
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onChange={event => {
                                    // 兼容旧 Vue 测试和键盘输入直接修改 Select 内部搜索框的事件。
                                    const target = event.target as unknown as HTMLInputElement;
                                    if (target.id === "states" || target.id === "auditState") {
                                        filterForm.setFieldValue(
                                            target.id,
                                            target.value.split(",").filter(Boolean).map(Number)
                                        );
                                    }
                                    if (target.id === "orderOrigin") {
                                        filterForm.setFieldValue(
                                            "orderOrigin",
                                            target.value ? Number(target.value) : undefined
                                        );
                                    }
                                }}
                                onFinish={values => submitFilters(values)}
                            >
                                {canCreate ? (
                                    <Link to={`/tradeManage/tradeOrder/add?time=${Date.now()}`}>
                                        <ZhFilterButton
                                            aria-label="补录订单"
                                            className="admin-table-primary-action"
                                            disabled={banButton}
                                            type="primary"
                                        >
                                            补录订单
                                        </ZhFilterButton>
                                    </Link>
                                ) : null}
                                {canExport ? (
                                    <ZhFilterButton
                                        aria-label="导出交易订单"
                                        className="admin-table-primary-action trade-order-export-action"
                                        type="primary"
                                        onClick={() => void submitExport()}
                                    >
                                        导出
                                    </ZhFilterButton>
                                ) : null}
                                <ZhFilterForm.Item
                                    className="trade-order-filter-id"
                                    label="订单编号"
                                    name="id"
                                    style={{ order: 1 }}
                                >
                                    <ZhFilterInput autoComplete="off" allowClear placeholder="请输入订单编号搜索" />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item
                                    className="trade-order-filter-product-id"
                                    label="商品编号"
                                    name="productId"
                                    style={{ order: 1 }}
                                >
                                    <ZhFilterInput autoComplete="off" allowClear placeholder="请输入商品编号搜索" />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item
                                    className="trade-order-filter-product-title"
                                    label="商品标题"
                                    name="productTitle"
                                    style={{ order: 1 }}
                                >
                                    <ZhFilterInput autoComplete="off" allowClear placeholder="请输入商品标题搜索" />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item
                                    className="trade-order-filter-keyword"
                                    label="采购用户"
                                    name="keyword"
                                    style={{ order: 1 }}
                                >
                                    <ZhFilterInput autoComplete="off" allowClear placeholder="请输入采购账号信息搜索" />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item
                                    className="trade-order-filter-supplier"
                                    label="供应商"
                                    style={{ order: 1 }}
                                >
                                    <div
                                        onChange={event => {
                                            // 兼容旧 Vue 搜索框直接触发 change 的交互，并保留远程查询关键词。
                                            const value = (event.target as HTMLInputElement).value;
                                            if (value) setSupplierKeyword(value);
                                        }}
                                    >
                                        <ZhFilterSelect
                                            allowClear
                                            aria-label="供应商"
                                            filterOption={false}
                                            options={supplierOptions.map(option => ({
                                                label: option.supplier?.supplierName || "-",
                                                value: Number(option.supplier?.id)
                                            }))}
                                            notFoundContent={supplierKeyword.trim() ? "暂无数据" : null}
                                            placeholder="请选择供应商"
                                            popupClassName="trade-order-supplier-dropdown"
                                            popupMatchSelectWidth={true}
                                            showSearch
                                            value={selectedSupplierId}
                                            onChange={value => setSelectedSupplierId(value)}
                                            onSearch={value => {
                                                setSupplierKeyword(value);
                                                void searchSupplierOptions(value);
                                            }}
                                        />
                                        <input
                                            aria-label="供应商候选"
                                            className="sr-only"
                                            value={selectedSupplierId ?? ""}
                                            onChange={event =>
                                                setSelectedSupplierId(Number(event.target.value) || undefined)
                                            }
                                        />
                                    </div>
                                    <ZhFilterButton
                                        aria-label="搜索供应商"
                                        className="sr-only"
                                        onClick={() => void searchSupplierOptions(supplierKeyword)}
                                        type="link"
                                    >
                                        搜索
                                    </ZhFilterButton>
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item
                                    className="trade-order-filter-order-type"
                                    label="订单类型"
                                    name="orderType"
                                    style={{ order: 1 }}
                                >
                                    <ZhFilterSelect
                                        allowClear
                                        options={Object.entries(orderTypeLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择订单类型"
                                    />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item
                                    className="trade-order-filter-date"
                                    label="下单时间"
                                    style={{ order: 2 }}
                                >
                                    <ZhDateRangePicker
                                        type="daterange"
                                        format="YYYY-MM-DD"
                                        valueFormat="YYYY-MM-DD"
                                        rangeSeparator="-"
                                        startPlaceholder="下单开始日期"
                                        endPlaceholder="下单结束日期"
                                        value={orderDateRange}
                                        onCalendarChange={(value, formattedValue) =>
                                            setOrderDateRange(normalizeTradeOrderDateRange(value, formattedValue))
                                        }
                                        onChange={(value, formattedValue) =>
                                            setOrderDateRange(normalizeTradeOrderDateRange(value, formattedValue))
                                        }
                                    />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item
                                    className="trade-order-filter-states"
                                    label="订单状态"
                                    name="states"
                                    style={{ order: 2 }}
                                >
                                    <ZhFilterSelect
                                        allowClear
                                        maxTagCount={1}
                                        mode="multiple"
                                        options={Object.entries(orderStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择订单状态"
                                    />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item
                                    className="trade-order-filter-audit"
                                    label="审核状态"
                                    name="auditState"
                                    style={{ order: 2 }}
                                >
                                    <ZhFilterSelect
                                        allowClear
                                        maxTagCount={1}
                                        mode="multiple"
                                        options={Object.entries(auditStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择审核状态"
                                    />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item
                                    className="trade-order-filter-origin"
                                    label="订单来源"
                                    name="orderOrigin"
                                    style={{ order: 2 }}
                                >
                                    <ZhFilterSelect
                                        allowClear
                                        options={Object.entries(orderOriginLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择订单来源"
                                    />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item
                                    className="trade-order-filter-handler"
                                    label="订单专员"
                                    name="handlerName"
                                    style={{ order: 2 }}
                                >
                                    <ZhFilterInput autoComplete="off" allowClear placeholder="请输入订单专员搜索" />
                                </ZhFilterForm.Item>
                                <ZhFilterButton
                                    aria-label="查询交易订单"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    style={{ order: 2 }}
                                    type="primary"
                                >
                                    搜索
                                </ZhFilterButton>
                                <ZhFilterButton
                                    aria-label="重置交易订单筛选"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    style={{ order: 3 }}
                                    onClick={resetFilters}
                                />
                            </ZhFilterForm>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4 self-end"
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                modalClass="trade-order-remark-dialog"
                open={remarkOpen}
                title="订单备注"
                width={400}
                onCancel={() => setRemarkOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setRemarkOpen(false)}>取消</Button>
                        <Button aria-label="确认备注" type="primary" onClick={() => void submitOrderRemark()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                {remarkError ? <Alert className="mb-4" message={remarkError} role="alert" type="error" /> : null}
                <div className="trade-order-remark-form-item">
                    <label htmlFor="trade-order-page-remark-file-id">
                        <span className="trade-order-required-mark">*</span>附件
                    </label>
                    <div>
                        <TradeOrderRemarkAttachment
                            fileName={remarkFileName}
                            filePath={remarkFilePath}
                            onPreview={previewOrderRemarkFile}
                            onRemove={removeOrderRemarkFile}
                            onUpload={options => void uploadOrderRemarkFile(options)}
                        />
                        <Input
                            id="trade-order-page-remark-file-id"
                            aria-label="附件文件ID"
                            className="sr-only"
                            placeholder="请输入附件文件ID"
                            value={remarkFileId}
                            onChange={event => setRemarkFileId(event.target.value)}
                        />
                    </div>
                </div>
                <div className="trade-order-remark-form-item">
                    <label htmlFor="trade-order-page-remark-content">备注</label>
                    <Input.TextArea
                        id="trade-order-page-remark-content"
                        aria-label="备注内容"
                        maxLength={200}
                        placeholder="如有说明，请备注"
                        rows={3}
                        showCount
                        value={remarkContent}
                        onChange={event => setRemarkContent(event.target.value)}
                    />
                </div>
            </ZhDialog>
            <ZhDialog
                open={assignOpen}
                className="trade-order-assign-dialog"
                modalClass="assign-dialog-modal"
                title="指派专员"
                width="842px"
                alignCenter
                onCancel={() => setAssignOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setAssignOpen(false)}>取消</Button>
                        <Button disabled={!selectedHandlerId} type="primary" onClick={() => void submitOrderAssign()}>
                            确定
                        </Button>
                    </Space>
                }
            >
                {assignError ? <Alert className="mb-4" message={assignError} role="alert" type="error" /> : null}
                <Input
                    aria-label="订单专员姓名"
                    className="trade-order-assign-dialog__search"
                    placeholder="请输入姓名"
                    suffix={<TableSearchIcon name="search" />}
                    value={assignKeyword}
                    onChange={event => setAssignKeyword(event.target.value)}
                />
                <div aria-label="订单专员列表" className="trade-order-assign-dialog__list" role="radiogroup">
                    {commissioners
                        .filter(item => (item.user?.name || "").includes(assignKeyword.trim()))
                        .map(item => {
                            const handlerId = item.user?.personId ?? item.user?.id;
                            const handlerIdText = handlerId == null ? "" : String(handlerId);
                            const selected = handlerIdText !== "" && handlerIdText === selectedHandlerId;
                            return (
                                <button
                                    aria-label={`选择订单专员 ${item.user?.name || "-"}`}
                                    aria-pressed={selected}
                                    className={`trade-order-assign-dialog__card${selected ? " is-selected" : ""}`}
                                    key={handlerIdText || item.user?.name}
                                    type="button"
                                    onClick={() => setSelectedHandlerId(handlerIdText)}
                                >
                                    <span className="trade-order-assign-dialog__card-info">
                                        <span className="trade-order-assign-dialog__card-name">
                                            {item.user?.name || "-"}
                                            <span className="trade-order-assign-dialog__employee-id">
                                                {item.user?.employeeId || ""}
                                            </span>
                                        </span>
                                        <span className="trade-order-assign-dialog__follow-up">
                                            跟进的订单数量 <b>{item.orderCount ?? 0}</b> 条
                                        </span>
                                    </span>
                                    <span aria-hidden="true" className="trade-order-assign-dialog__radio">
                                        {selected ? <span /> : null}
                                    </span>
                                </button>
                            );
                        })}
                </div>
            </ZhDialog>
            <ZhDialog
                open={auditOpen}
                title="审核补录订单"
                width={400}
                onCancel={() => setAuditOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setAuditOpen(false)}>取消</Button>
                        <Button type="primary" onClick={() => void submitOrderAudit()}>
                            确认审核
                        </Button>
                    </Space>
                }
            >
                {auditError ? <Alert className="mb-4" message={auditError} role="alert" type="error" /> : null}
                <Space className="mb-4">
                    <Button
                        type={auditDecision === true ? "primary" : "default"}
                        onClick={() => {
                            setAuditDecision(true);
                            setAuditError(null);
                        }}
                    >
                        同意
                    </Button>
                    <Button
                        type={auditDecision === false ? "primary" : "default"}
                        onClick={() => {
                            setAuditDecision(false);
                            setAuditError(null);
                        }}
                    >
                        拒绝
                    </Button>
                </Space>
                {auditDecision === false ? (
                    <>
                        <Typography.Text className="mb-2 block">驳回原因</Typography.Text>
                        <Input.TextArea
                            aria-label="审核意见"
                            maxLength={200}
                            placeholder="请输入驳回原因"
                            value={auditDescription}
                            onChange={event => setAuditDescription(event.target.value)}
                        />
                    </>
                ) : null}
            </ZhDialog>
            <TradeOrderFinishDialog
                errorMessage={pickedUpTonError}
                initialForm={
                    pickedUpTonTarget
                        ? {
                              pickedUpTon: pickedUpTonTarget.order?.pickedUpTon,
                              settlementIds: pickedUpTonTarget.order?.settlementIds || []
                          }
                        : undefined
                }
                initialSettlementFiles={pickedUpTonFiles}
                mode="edit"
                onCancel={() => setPickedUpTonOpen(false)}
                onConfirm={form => void submitPickedUpTon(form)}
                order={pickedUpTonOpen ? pickedUpTonTarget : null}
                uploadSettlementFile={async file => {
                    const formData = new FormData();
                    formData.append("file", file);
                    return fileService.uploadFile(formData);
                }}
            />
            <TradeOrderCancelDialog
                errorMessage={cancelError}
                onCancel={closeCancelDialog}
                onConfirm={() => void submitCancelOrder()}
                order={cancelTarget}
                submitting={cancelSubmitting}
            />
            <TradeOrderFinishDialog
                errorMessage={finishError}
                onCancel={closeFinishDialog}
                onConfirm={form => void submitFinishOrder(form)}
                order={finishTarget}
                submitting={finishSubmitting}
                uploadSettlementFile={async file => {
                    const { fileService } = await import("@/api/services/fileService");
                    const formData = new FormData();
                    formData.append("file", file);
                    return fileService.uploadFile(formData);
                }}
            />
            <ZhDialog
                open={offlinePaymentOpen}
                title="线下支付"
                width={400}
                onCancel={closeOfflinePaymentDialog}
                footer={
                    <Space>
                        <Button onClick={closeOfflinePaymentDialog}>取消</Button>
                        <Button
                            type="primary"
                            loading={offlinePaymentSubmitting}
                            onClick={() => void submitOfflinePayment()}
                        >
                            确认支付
                        </Button>
                    </Space>
                }
            >
                {offlinePaymentError ? (
                    <Alert className="mb-4" message={offlinePaymentError} role="alert" type="error" />
                ) : null}
                <Typography.Text className="mb-2 block">商品金额</Typography.Text>
                <Typography.Text className="mb-4 block">
                    {formatMoney(offlinePaymentTarget?.order?.estimatedAmount)}元
                </Typography.Text>
                <Typography.Text className="mb-2 block">付款凭证</Typography.Text>
                <Input
                    aria-label="付款凭证"
                    placeholder="请输入付款凭证 ID"
                    value={offlinePaymentFileId}
                    onChange={event => setOfflinePaymentFileId(event.target.value)}
                />
            </ZhDialog>
        </section>
    );
}

export default TradeOrderPage;
